import React, { useState, useEffect, useMemo, memo, useCallback } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
    Clock,
    Shield,
    CheckCircle,
    ArrowRight,
    Users,
    Smartphone,
    MapPin,
    TrendingUp,
    Star,
    Zap,
    Search,
    AlertCircle,
    Loader2,
    Award,
    Target,
    Database,
    Globe,
    Rocket,
    Lock,
    Activity,
    Wifi,
    Briefcase,
    Building,
    Sparkles,
    Eye,
    Instagram,
    Cpu,
    RefreshCcw
} from 'lucide-react';
import { FilledIcon } from '../components/icons/FilledIcon';
import { useAuthStore } from '../stores/useAuthStore';
import { supabase } from '../lib/supabase';
import { profileLookupErrorMessage } from '../lib/functionErrorMessage';
import toast from 'react-hot-toast';
import { getMyConnectedUsername, CONNECT_FIRST_MESSAGE, CONNECT_FIRST_PATH, SELF_ANALYSIS_MESSAGE } from '../lib/myInstagramAccount';
import HeroSection from '../components/home/HeroSection';
import DataScrapingShowcase from '../components/home/DataScrapingShowcase';
import OtherServiceCTAs from '../components/home/OtherServiceCTAs';
import '../styles/animations.css';
import CountUp from '../components/ui/CountUp';
import ScrollVelocity from '../components/ui/ScrollVelocity';
import HowItWorksSteps from '../components/home/HowItWorksSteps';
import DataDotMatrix from '../components/home/DataDotMatrix';
import { IG_GRADIENT, accent, gradientText, tint } from '../components/home/brandAccent';
import { Link2 } from 'lucide-react';
import { CountUp as RiseCount } from '../components/vip/VipPlanMotion';
import TrueFocus from '../components/ui/TrueFocus';
import TestimonialsColumn, { Testimonial } from '../components/testimonials/testimonials-column';
import { testimonials as rawTestimonials } from '../data/testimonials';
import ReviewToast from '../components/review/ReviewToast';

// UI Tokens and types for this page
type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

interface ExpertiseItem {
    readonly icon: IconComponent;
    readonly title: string;
    readonly description: string;
}

const THREADPULSE_TOKENS = {
    // Background layers
    layoutBg: 'bg-[#000000]',
    layoutDivider: 'border-[#262626]',
    surface: 'bg-[#101010]',
    surfaceElevated: 'bg-[#181818]',
    surfaceMuted: 'bg-[#1F1F1F]',

    // Card styling
    cardBg: 'bg-[#101010]',
    cardBorder: 'border-[#262626]',
    cardHover: 'hover:bg-[#181818]',
    glassBorder: 'border-[#262626]/80 backdrop-blur-xl',

    // Accent gradients
    accentPrimaryFrom: 'from-[#3182F6]',
    accentPrimaryTo: 'to-[#4FC3F7]',
    accentSecondaryFrom: 'from-[#8B5CF6]',
    accentSecondaryTo: 'to-[#A855F7]',

    // Legacy aliases for existing JSX usage
    content1: 'bg-[#101010]',
    cardHoverBg: 'hover:bg-[#181818]',
    primaryFrom: 'from-[#3182F6]',
    primaryTo: 'to-[#4FC3F7]',
    secondaryFrom: 'from-[#8B5CF6]',
    secondaryTo: 'to-[#A855F7]',
    primaryBg: 'bg-[#3182F6]',
    textTertiary: 'text-[#A8A8A8]',
    shadowLg: 'shadow-lg',
    shadowXl: 'shadow-xl',

    // Typography
    textPrimary: 'text-[#FFFFFF]',
    textSecondary: 'text-[#A8A8A8]',
    textMuted: 'text-[#777777]',

    // Radius scale
    roundedLg: 'rounded-lg',
    roundedXl: 'rounded-xl',
    rounded2xl: 'rounded-2xl',
    rounded3xl: 'rounded-3xl'
} as const;

const SectionHeader: React.FC<{
    eyebrow?: string;
    title: React.ReactNode;
    description?: string;
    align?: 'left' | 'center';
}> = ({ eyebrow, title, description, align = 'left' }) => {
    const alignment = align === 'center' ? 'items-center text-center' : 'items-start text-left';
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className={`flex flex-col gap-4 ${alignment}`}
        >
            {eyebrow && (
                <span className="inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-[#A1A1AA]">
                    <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundImage: IG_GRADIENT }} />
                    {eyebrow}
                </span>
            )}
            <div className="flex flex-col gap-4">
                <h2 className="text-[30px] sm:text-[44px] font-bold text-[#FAFAFA] break-keep" style={{ lineHeight: 1.18, letterSpacing: '-0.03em', textWrap: 'balance' as never }}>
                    {title}
                </h2>
                {description && (
                    <p className="text-[16px] sm:text-[18px] text-[#A1A1AA] leading-relaxed max-w-3xl break-keep">
                        {description}
                    </p>
                )}
            </div>
        </motion.div>
    );
};

// React Bits 원칙: 순수 컴포넌트 패턴으로 메모이제이션 최적화  


// Design System: Feature Card with Figma design rules
const FeatureCard = memo(({ feature, index }: { feature: any; index: number }) => (
    <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 * index }}
        whileHover={{ scale: 1.02, y: -8 }}
        className="group h-full"
    >
        <div className={`h-full ${THREADPULSE_TOKENS.content1} ${THREADPULSE_TOKENS.cardBorder} ${THREADPULSE_TOKENS.rounded2xl} p-6 ${THREADPULSE_TOKENS.cardHoverBg} transition-all duration-300 backdrop-blur-sm relative overflow-hidden`}>
            {/* 그라디언트 배경 효과 */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#8B5CF6]/5 to-[#EC4899]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

            <div className={`w-14 h-14 bg-gradient-to-br ${THREADPULSE_TOKENS.primaryFrom}/20 ${THREADPULSE_TOKENS.secondaryTo}/20 ${THREADPULSE_TOKENS.roundedXl} flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-all duration-300 shadow-lg`}>
                {feature.icon ? (
                    <feature.icon className={`w-7 h-7 ${THREADPULSE_TOKENS.textPrimary}`} />
                ) : (
                    <FilledIcon name={feature.iconName} size={28} className={THREADPULSE_TOKENS.textPrimary} />
                )}
            </div>
            <h3 className={`text-large font-semibold ${THREADPULSE_TOKENS.textPrimary} mb-3 text-center`}>
                {feature.title}
            </h3>
            <p className={`${THREADPULSE_TOKENS.textTertiary} text-small leading-relaxed text-center`}>
                {feature.description}
            </p>
        </div>
    </motion.div>
));

// Design System: Testimonial Card with glassmorphism
const TestimonialCard = memo(({ testimonial, index }: { testimonial: any; index: number }) => (
    <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15 * index }}
        className="group hover:scale-105 transition-all duration-300"
        whileHover={{ y: -10 }}
    >
        <div className={`h-full bg-gradient-to-br from-[#8B5CF6]/10 to-[#EC4899]/10 backdrop-blur-xl border border-[#8B5CF6]/20 ${THREADPULSE_TOKENS.rounded2xl} p-6 hover:from-[#8B5CF6]/15 hover:to-[#EC4899]/15 transition-all duration-300 shadow-lg hover:shadow-xl`}>
            <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: testimonial.rating }, (_, i) => (
                    <FilledIcon key={i} name="star" size={18} className="text-[#FACC15]" />
                ))}
            </div>
            <p className={`${THREADPULSE_TOKENS.textPrimary} mb-5 italic leading-relaxed text-base`}>
                "{testimonial.content}"
            </p>
            <div className="flex items-center gap-3">
                <div className={`w-10 h-10 bg-gradient-to-br ${THREADPULSE_TOKENS.primaryFrom} ${THREADPULSE_TOKENS.secondaryTo} rounded-full flex items-center justify-center shadow-md`}>
                    <span className={`text-sm font-bold ${THREADPULSE_TOKENS.textPrimary}`}>
                        {testimonial.name.charAt(0)}
                    </span>
                </div>
                <div>
                    <div className={`${THREADPULSE_TOKENS.textPrimary} font-semibold`}>
                        {testimonial.name}
                    </div>
                    <div className={`${THREADPULSE_TOKENS.textMuted} text-small`}>
                        {testimonial.username}
                    </div>
                </div>
            </div>
        </div>
    </motion.div>
));


/*
 * 홈 지표 이력:
 * - ~2026-08-11: 조회값에 가산 + Math.random() 난수 → 표시광고법 리스크로 폐기, 실측 내림값으로 교체.
 * - 2026-08-17 (다니엘 지시): 누적 분석·가입 회원·수집 계정 등 **규모 숫자 전면 비노출** —
 *   사업 규모를 외부에 광고하는 것이라 제거. 가치 지표(1~2분·24시간·6가지)로 교체, 전부 실측·상품 정의 근거.
 *   규모·이용자 수 숫자는 다시 넣지 않는다.
 */
const HomePage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();

    // React Bits 원칙: State 최적화 및 분리
    const [targetUsername, setTargetUsername] = useState('');
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysisStep, setAnalysisStep] = useState(0);
    const [error, setError] = useState('');

    // React Bits 원칙: useMemo로 계산 비용 최적화
    const featuresData = useMemo(() => [
        // 2026-08-11 문구 정정(다니엘 지시) — 아래 셋 모두 실제 동작과 어긋나 있었다:
        //  · '실시간으로 분석' → 산출 기준은 지난 24시간이다.
        //  · '새로운 방문자가 있을 때마다 즉시 알림' → 방문을 감지해 자동 발송하는 장치는 없다.
        //    웹푸시는 관리자가 캠페인(push_campaigns)으로 보내는 구조다.
        //  · '계정 정보는 절대 저장하지 않는' → 실제로는 인스타 계정 데이터를 23만~29만 건 보관한다.
        //    사실인 것은 "비밀번호를 저장하지 않는다"이며, '100%' 같은 절대 표현도 뺀다.
        {
            icon: Search,
            title: '24시간 방문자 분석',
            description: '지난 24시간을 기준으로 방문 흐름을 분석합니다',
            color: 'text-white',
            bgColor: 'bg-white/10'
        },
        {
            icon: Clock,
            title: '분석 알림',
            description: '분석 결과와 주요 변화를 알림으로 받아보기',
            color: 'text-white',
            bgColor: 'bg-white/10'
        },
        {
            iconName: 'shield' as const,
            title: '보안 우선',
            description: 'Instagram 비밀번호는 저장하지 않는 보안 우선 서비스',
            color: 'text-white',
            bgColor: 'bg-white/10'
        }
    ], []);

    // New services data for redesigned section
    const services: ReadonlyArray<ExpertiseItem> = useMemo(() => ([
        // 2026-08-11 문구 정정(다니엘 지시):
        //  · '추적'은 지속 감시를 함의해 불필요한 오해를 산다. 우리가 실제로 하는 일은 요청 시점의 수집이다.
        //  · '재방문'은 인스타가 알려주지 않아 우리가 직접 측정할 수 없는 값이다.
        //    실제로 보는 것은 좋아요·댓글·태그·팔로우 관계 = 상호작용과 활동 신호다.
        {
            icon: Activity,
            title: '실시간 마이닝',
            description: '24시간 언제든 데이터 스크래핑 및 수집이 가능합니다.'
        },
        {
            icon: Target,
            title: 'AI 인사이트',
            description: '상호작용·활동 신호를 AI로 분류해 의미 있는 인사이트를 제공합니다.'
        },
        {
            icon: Shield,
            title: '프라이버시 우선',
            description: '계정 비밀번호 저장 없이 안전하게 분석합니다.'
        }
    ]), []);

    const testimonialsData = useMemo(() => rawTestimonials.map(t => ({ ...t, rating: t.rating ?? 5 })), []);

    // 사용자 후기 마키(세로) 컬럼 데이터 매핑 + 개인정보 가림처리
    const maskText = (value: string): string => {
        if (!value) return '';
        const visible: number = Math.max(1, Math.floor(value.length / 3));
        const masked: string = '*'.repeat(Math.max(2, value.length - visible));
        return value.slice(0, visible) + masked;
    };

    const anonymizedTestimonials: ReadonlyArray<Testimonial> = useMemo(() => (
        testimonialsData.map((t) => ({
            text: `"${t.content}"`,
            name: maskText(t.name),
            role: maskText(t.username),
            imageUrl: undefined
        }))
    ), [testimonialsData]);

    const testimonialColumns = useMemo(() => {
        if (anonymizedTestimonials.length === 0) return [[], [], []] as ReadonlyArray<ReadonlyArray<Testimonial>>;
        const perColumn = Math.max(8, Math.ceil(anonymizedTestimonials.length / 3));
        return Array.from({ length: 3 }, (_, index) => {
            const start = index * perColumn;
            const slice = anonymizedTestimonials.slice(start, start + perColumn);
            return slice.length ? slice : anonymizedTestimonials;
        });
    }, [anonymizedTestimonials]);

    const rotateTestimonials = (list: ReadonlyArray<Testimonial>, offset: number) => {
        if (!list.length) return list;
        const shift = offset % list.length;
        return list.slice(shift).concat(list.slice(0, shift));
    };

    const marqueeCol1: ReadonlyArray<Testimonial> = testimonialColumns[0];
    const marqueeCol2: ReadonlyArray<Testimonial> = useMemo(() => rotateTestimonials(testimonialColumns[1], 3), [testimonialColumns]);
    const marqueeCol3: ReadonlyArray<Testimonial> = useMemo(() => rotateTestimonials(testimonialColumns[2], 6), [testimonialColumns]);


    const handleQuickAnalysis = useCallback(async () => {
        if (!targetUsername.trim()) {
            setError('Instagram 아이디를 입력해주세요');
            return;
        }

        if (!user) {
            navigate('/auth', { state: { targetUsername } });
            return;
        }

        // 내 계정 연결이 먼저(대표 11/3) — 분석 대상 프로필을 불러오면 그 순간 분석 기록이 생기므로 그 전에 확인한다
        const myUsername = await getMyConnectedUsername(user.id);
        if (!myUsername) {
            toast(CONNECT_FIRST_MESSAGE, { icon: <Link2 className="h-4 w-4 text-[#4C9AFF]" />, duration: 6000 });
            navigate(CONNECT_FIRST_PATH);
            return;
        }
        if (myUsername === targetUsername.trim().replace('@', '').toLowerCase()) {
            setError(SELF_ANALYSIS_MESSAGE);
            return;
        }

        setError('');
        setIsAnalyzing(true);
        setAnalysisStep(0);

        try {
            // 실제 Instagram 프로필 검증
            const interval = setInterval(() => {
                setAnalysisStep(prev => prev < 3 ? prev + 1 : prev);
            }, 1000);

            const { data, error: functionError } = await supabase.functions.invoke('visitor-analysis-target', {
                body: { instagramIDvalue: targetUsername.trim().replace('@', '') }
            });

            clearInterval(interval);

            // 서버 오류는 사유별 한국어 안내 (2026-10-28 대표 승인 — 전에는 일시 오류도 '찾을 수 없습니다'로 보였다)
            if (functionError) throw new Error(await profileLookupErrorMessage(functionError, targetUsername.trim().replace('@', '')));
            if (!data?.profile_data) {
                throw new Error('Instagram 프로필을 찾을 수 없습니다. 아이디를 확인해주세요.');
            }

            // 방문자 분석 페이지로 이동 (결제 포함)
            navigate('/visitor-analysis', {
                state: {
                    targetUsername: targetUsername.trim().replace('@', ''),
                    profileData: data.profile_data
                }
            });

        } catch (error: any) {
            setError(error.message || '분석 중 오류가 발생했습니다');
            setIsAnalyzing(false);
            setAnalysisStep(0);
        }
    }, [targetUsername, user, navigate]);

    return (
        <div className="min-h-screen bg-black overflow-x-hidden w-full">
            {/* 비로그인 손님에게 — 예전 지어낸 '활동 알림' 자리에 대표가 고른 진짜 후기(10/28 대표 "넣어봐 응해") */}
            <ReviewToast enabled={!user} initialDelay={3} displayDuration={8} desktop="bottom" />

            {/* Hero Section */}
            <HeroSection
                user={user}
                targetUsername={targetUsername}
                setTargetUsername={setTargetUsername}
                isAnalyzing={isAnalyzing}
                error={error}
                analysisStep={analysisStep}
                handleQuickAnalysis={handleQuickAnalysis}
                navigate={navigate}
            />

            {/* Scroll Velocity - Core Values */}
            <div className="py-7 md:py-10 border-y border-white/[0.07] bg-black">
                <ScrollVelocity
                    texts={[
                        // '실시간 확인'·'100%'는 실제 산출(지난 24시간 기준 분석)과 어긋난다 — 2026-08-11 정정
                        "내 피드 관심도 AI 인사이트 • 24시간 기준 분석 • 내 피드 관심도 AI 인사이트 • 24시간 기준 분석 •",
                        "안전한 소셜미디어 인사이트 • 지금 바로 분석 가능한 소셜미디어 인사이트 • 안전한 소셜미디어 인사이트 • 지금 바로 분석 가능한 소셜미디어 인사이트 •"
                    ]}
                    velocity={25}
                    className="text-[#71717A] font-bold uppercase tracking-wide"
                    numCopies={2}
                    damping={20}
                    stiffness={150}
                />
            </div>

            {/* 다른 서비스 소개: 팔로워 레이더 → 블랭킷 전화 상담 (MASK LAB은 10/22 대표 지시로 내림 — components/home/OtherServiceCTAs) */}
            <OtherServiceCTAs />

            <DataScrapingShowcase />

            {/* Expertise Section - 디자인 시스템 적용 */}
            <motion.section
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.8 }}
                className="relative overflow-hidden py-16 sm:py-24"
            >

                <div className="relative mx-auto flex max-w-7xl flex-col gap-12 px-5 sm:gap-16 sm:px-6 lg:px-8">
                    <SectionHeader
                        eyebrow="Core Capabilities"
                        title={(<>
                            소셜 미디어 인사이트 제공{' '}
                            <span style={gradientText}>Full-Stack</span> 서비스
                        </>)}
                        description="인스타그램 인사이트/알림/IT 테크에 특화된 SaaS. 안전하게, 빠르게 결과를 제공합니다."
                    />

                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        {/* Left: Big highlight card - 디자인 시스템 적용 */}
                        <motion.div
                            initial={{ opacity: 0, y: 32 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                            className="group relative overflow-hidden rounded-[28px] border border-white/[0.08] bg-[#0F0F11] p-7 sm:p-10"
                        >
                            {/* 데이터 점무늬(글자 없음) — 오른쪽 위에서 물결처럼 반짝인다 */}
                            <DataDotMatrix className="absolute -right-2 -top-1 opacity-80 sm:right-2 sm:top-2" />

                            <div className="relative z-10 space-y-6">
                                <div className="inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-[#A1A1AA]">
                                    <FilledIcon name="sparkles" size={15} className="text-[#4C9AFF]" />
                                    Verified Impact
                                </div>
                                {/* '방문 데이터 20만 건 보유'는 우리가 방문을 직접 측정한다는 주장이 되어 사실과 다르다.
                                    실제로 보유한 것은 수집한 인스타그램 계정 데이터로, 2026-08-11 실측 296,536건이다.
                                    '20만+'는 실측보다 낮게 잡은 표기라 항상 참이다. (다니엘 지시, 2026-08-11) */}
                                <h3 className="text-[28px] sm:text-[44px] font-bold text-[#FAFAFA] break-keep" style={{ lineHeight: 1.18, letterSpacing: '-0.03em', textWrap: 'balance' as never }}>
                                    <RiseCount to={20} duration={1.2} delay={0.2} />만+ 인스타그램 데이터
                                    <br />
                                    <span style={gradientText}>마이닝 경험</span>
                                </h3>
                                <p className="text-[15px] sm:text-[16px] text-[#A1A1AA] leading-relaxed max-w-xl break-keep">
                                    실제 인스타그램 사용자 데이터를 기반으로 인사이트를 제공합니다.
                                </p>
                                <motion.button
                                    onClick={() => navigate('/visitor-analysis')}
                                    className="inline-flex h-[54px] w-full items-center justify-center gap-2 rounded-full bg-white px-8 text-[16px] font-semibold text-[#0A0A0A] transition-colors hover:bg-white/90 sm:w-auto"
                                    whileTap={{ scale: 0.98 }}
                                >
                                    지금 분석 시작하기
                                </motion.button>
                            </div>
                        </motion.div>

                        {/* Right: three stacked service cards - 디자인 시스템 적용 */}
                        {/* 휴대폰: 좌우로 넘김 / PC: 세로 세 칸 */}
                        <div className="-mx-5 flex snap-x snap-mandatory gap-3 overflow-x-auto px-5 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:-mx-6 sm:px-6 lg:mx-0 lg:grid lg:grid-cols-1 lg:gap-4 lg:overflow-visible lg:px-0" style={{ scrollPaddingLeft: 20 }}>
                            {services.map((item, idx) => (
                                <motion.div
                                    key={item.title}
                                    initial={{ opacity: 0, x: 24 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: 0.15 * idx }}
                                    className="relative w-[80%] shrink-0 snap-start overflow-hidden rounded-[24px] border border-white/[0.08] bg-[#0F0F11] p-6 sm:w-[46%] sm:p-7 lg:w-auto"
                                >
                                    <div className="relative flex items-start gap-4 sm:gap-5">
                                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full" style={{ background: tint(accent(idx)), color: accent(idx) }}>
                                            <item.icon className="h-5 w-5" />
                                        </div>
                                        <div className="flex-1 space-y-1.5">
                                            <h4 className="text-[18px] font-semibold text-[#FAFAFA]">{item.title}</h4>
                                            <p className="text-[14px] sm:text-[15px] text-[#A1A1AA] leading-relaxed break-keep">{item.description}</p>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* Trust & Credentials Section - 디자인 시스템 개선 */}
            <motion.section
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="relative px-4 pb-20 sm:px-6 lg:px-8"
            >

                <div className="relative mx-auto flex max-w-6xl flex-col gap-12">




                </div>
            </motion.section>
            {/* How It Works - 디자인 시스템 개선 */}
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="mx-auto mb-20 max-w-7xl px-5 sm:px-6 lg:px-8"
            >
                <h2 className="mb-10 text-left text-[32px] font-bold text-[#FAFAFA] sm:mb-16 sm:text-[44px]" style={{ lineHeight: 1.15, letterSpacing: '-0.03em' }}>
                    이용 방법
                </h2>

                <HowItWorksSteps />
            </motion.div>

            {/* Testimonials - 디자인 시스템 개선 */}
            <motion.section
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="mb-32 py-20 relative"
            >

                <div className="relative">
                    <div className="mx-auto mb-10 max-w-7xl px-5 text-left sm:mb-12 sm:px-6 lg:px-8">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="mb-4 inline-flex items-center gap-2"
                        >
                            <Star className="w-3.5 h-3.5 text-[#3182F6]" />
                            <span className="text-[13px] font-semibold tracking-[0.04em] text-[#A1A1AA]">By The Numbers</span>
                        </motion.div>
                        <h2 className="mb-3 text-[30px] font-bold text-[#FAFAFA] sm:mb-4 sm:text-[44px] break-keep" style={{ lineHeight: 1.18, letterSpacing: '-0.03em', textWrap: 'balance' as never }}>
                            숫자로 보는 <span style={gradientText}>소셜데이터랩</span>
                        </h2>
                        <p className="text-[#A1A1AA] text-[16px] sm:text-[18px] max-w-2xl leading-relaxed break-keep">
                            지어낸 후기 대신, 실제 서비스 기록으로 말씀드립니다
                        </p>
                    </div>

                    {/*
                      ★ 2026-08-11 (다니엘 지시) — 이 자리에는 후기 53개가 흐르고 있었다.
                      전부 코드(src/data/testimonials.ts)에 미리 적어둔 문구였는데 화면에는
                      "실제 고객들의 피드백"이라고 표기했다. 거짓 체험기는 표시광고법에서 가장 다투기 어려운
                      유형이고, 후기 내용에 우리가 제공하지 않는 기능(자동 리포트 메일 등)까지 섞여 있었다.
                      그래서 사회적 증거를 '지어낸 말'에서 '실제 기록'으로 바꾼다.

                      ★ 2026-08-17 (다니엘 지시) — 규모 숫자(누적 분석·가입 회원·수집 계정·유료 이용자)
                      전면 비노출: 사업 규모를 외부에 광고하는 것이라 제거했다. 아래는 가치 지표로,
                      전부 실측·상품 정의 근거다(1~2분 = 8/10 실측 37~106초 / 24시간 = 상품 정의 /
                      6가지 = 대시보드 실제 기능 수 / 알림 = 웹푸시 실기능). 규모·이용자 수는 다시 넣지 않는다.
                    */}
                    <div className="mx-auto grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-8 max-w-7xl px-5 sm:px-6 lg:px-8">
                        {[
                            { value: '1~2분', label: '평균 분석 완료' },
                            { value: '24시간', label: '신속 결과 발급' }, // '추적'은 위협적 어감 — 다니엘 교정 2026-08-17
                            { value: '6가지', label: '계정 인사이트' },
                            { value: '실시간', label: '변화 알림' },
                        ].map((stat, i) => (
                            <motion.div
                                key={stat.label}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.08 }}
                                className="relative pt-5 text-left"
                            >
                                <span aria-hidden className="absolute inset-x-0 top-0 h-px opacity-70" style={{ backgroundImage: IG_GRADIENT }} />
                                <p className="text-[34px] sm:text-[44px] font-bold text-[#FAFAFA] tracking-tight" style={{ fontVariantNumeric: 'tabular-nums' }}>{stat.value}</p>
                                <p className="mt-1.5 text-[13px] sm:text-[14px] text-[#A1A1AA]">{stat.label}</p>
                            </motion.div>
                        ))}
                    </div>

                    <p className="mx-auto mt-8 max-w-7xl px-5 text-left text-[12px] text-[#8B8B94] sm:px-6 lg:px-8">
                        분석 소요 시간은 실측 기준(2026년 8월)이며 계정 규모에 따라 달라질 수 있어요
                    </p>
                </div>
            </motion.section>

            {/* Enterprise Features Section - 디자인 시스템 개선 */}
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.1 }}
                className="mb-20"
            >



                {/* Performance Metrics
                    2026-08-11 정정(다니엘 지시) — 근거를 댈 수 없는 스펙이 나열돼 있었다:
                     · '2.5초 평균 분석 완료' → 실측은 37~106초(2026-08-10 직접 측정). 15~40배 과장이었다.
                     · '150+ 동시 처리', '500TB+ 데이터 처리 가능량' → 산출 근거를 찾지 못했다.
                     · '무중단 모니터링' → 상시 감시하는 기능은 없다. 사실인 것은 "언제든 분석을 시작할 수 있다"이다.
                    수치를 다시 바꿀 일이 있으면 반드시 실측값을 확보한 뒤 그보다 보수적으로 적을 것. */}
                <div className="mx-auto grid max-w-7xl grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-8 mt-12 px-5 sm:px-6 lg:px-8">
                    {[
                        {
                            metric: '1~2분',
                            description: '평균 분석 소요 시간',
                            icon: Clock
                        },
                        {
                            // 2026-08-17: 규모 숫자(가입 회원·수집 계정) 비노출 — 가치 지표로 교체
                            metric: '6가지',
                            description: '계정 인사이트 제공',
                            icon: Users
                        },
                        {
                            metric: '24시간',
                            description: '언제든 분석 시작 가능',
                            icon: Wifi
                        },
                        {
                            metric: '실시간',
                            description: '변화 알림',
                            icon: Database
                        }
                    ].map((item, index) => (
                        <motion.div
                            key={item.metric}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.1 * index }}
                            className="border-t border-white/[0.07] pt-4 text-left"
                        >
                            <item.icon className="w-5 h-5 mb-3" style={{ color: accent(index) }} />
                            <div className="text-[22px] sm:text-[24px] font-bold text-[#FAFAFA] mb-1" style={{ fontVariantNumeric: 'tabular-nums' }}>{item.metric}</div>
                            <div className="text-[13px] text-[#71717A]">{item.description}</div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* Core Focus Section with TrueFocus */}
            <section className="py-24 sm:py-32 relative overflow-hidden border-t border-white/[0.07]">
                <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8 relative">
                    <motion.div
                        initial={{ opacity: 0, y: 32 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="text-left mb-14 sm:mb-20"
                    >
                        <div className="mb-4 inline-flex items-center gap-2">
                            <Sparkles className="w-3.5 h-3.5 text-[#4C9AFF]" />
                            <span className="text-[13px] font-semibold tracking-[0.04em] text-[#A1A1AA]">Core Focus</span>
                        </div>
                        <h2 className="mb-4 text-[30px] font-bold text-[#FAFAFA] sm:text-[44px] break-keep" style={{ lineHeight: 1.18, letterSpacing: '-0.03em', textWrap: 'balance' as never }}>
                            우리가 집중하는 <span style={gradientText}>핵심 영역</span>
                        </h2>
                        <p className="text-[16px] sm:text-[18px] text-[#A1A1AA] max-w-3xl leading-relaxed break-keep">
                            소셜데이터랩의 전문성이 발휘되는 분야를 확인해보세요
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2, delay: 0.3 }}
                        className="flex justify-center items-center min-h-[200px]"
                    >
                        <TrueFocus
                            sentence="데이터 분석 소셜미디어 인사이트 AI기술 혁신"
                            manualMode={false}
                            blurAmount={8}
                            borderColor="#4C9AFF"
                            glowColor="rgba(76, 154, 255, 0.55)"
                            animationDuration={1.5}
                            pauseBetweenAnimations={2}
                            className="text-center"
                        />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.8 }}
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-5 mt-14 sm:mt-20"
                    >
                        {[
                            {
                                icon: 'activity',
                                title: '데이터 분석',
                                description: '복잡한 데이터를 의미있는 인사이트로 변환',
                                gradient: 'from-[#3182F6]/30 to-[#4FC3F7]/30',
                                borderColor: 'border-[#3182F6]/30',
                                iconColor: 'text-[#3182F6]'
                            },
                            {
                                icon: 'users',
                                title: '소셜미디어',
                                description: 'Instagram 등 소셜 플랫폼 전문 분석',
                                gradient: 'from-[#00D87E]/30 to-[#00BF72]/30',
                                borderColor: 'border-[#00D87E]/30',
                                iconColor: 'text-[#00D87E]'
                            },
                            {
                                icon: 'eye',
                                title: '인사이트',
                                description: '실행 가능한 통찰력 제공',
                                gradient: 'from-[#A855F7]/30 to-[#9333EA]/30',
                                borderColor: 'border-[#A855F7]/30',
                                iconColor: 'text-[#A855F7]'
                            },
                            {
                                icon: 'cpu',
                                title: 'AI기술',
                                description: '최신 인공지능 기술 활용',
                                gradient: 'from-[#FFB800]/30 to-[#FFA000]/30',
                                borderColor: 'border-[#FFB800]/30',
                                iconColor: 'text-[#FFB800]'
                            },
                            {
                                icon: 'rocket',
                                title: '혁신',
                                description: '끊임없는 기술 혁신 추구',
                                gradient: 'from-[#4FC3F7]/30 to-[#29B6F6]/30',
                                borderColor: 'border-[#4FC3F7]/30',
                                iconColor: 'text-[#4FC3F7]'
                            }
                        ].map((item, idx) => (
                            <motion.div
                                key={item.title}
                                className="rounded-[24px] border border-white/[0.08] bg-[#0F0F11] p-6 sm:p-7 text-left relative overflow-hidden"
                            >
                                <div className="relative">
                                    <div className="w-11 h-11 rounded-full flex items-center justify-center mb-5" style={{ background: tint(accent(idx)) }}>
                                        <FilledIcon name={item.icon as any} size={22} color={accent(idx)} />
                                    </div>
                                    <h3 className="text-[18px] font-bold text-[#FAFAFA] mb-2">{item.title}</h3>
                                    <p className="text-[14px] text-[#A1A1AA] leading-relaxed break-keep">{item.description}</p>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Media Coverage & Social Proof */}
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.15 }}
                className="mb-24 px-6"
            >




            </motion.div>




        </div>
    );
};

export default HomePage; 
